import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const MYT_TIMEZONE = "Asia/Kuala_Lumpur";

export function parseFormat(dateTime: string, format: string) {
  return dayjs.utc(dateTime).local().format(format);
}

export function parseFormatRaw(dateTime: string, format: string) {
  // Strip trailing 'Z' or timezone offset to prevent UTC-to-local conversion
  const raw = dateTime.replace(/Z$/, "").replace(/[+-]\d{2}:\d{2}$/, "");
  return dayjs(raw).format(format);
}

export const formatDate = (dateString: string) => {
  // format 2025-04-06T17:08:32.848805 and return just the date part with same formatting
  // remove any trailing 'Z' or timezone information
  if (dateString.endsWith("Z")) {
    dateString = dateString.slice(0, -1);
  }

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Formats a date string into a human-readable format
 * @param dateTime - The date string to format
 * @param includeTime - Whether to include the time in the output (default: false)
 * @returns A human-readable date string
 */
export function formatHumanReadable(
  dateTime: string,
  includeTime: boolean = false
): string {
  if (!dateTime) return "";

  // remove any trailing 'Z' or timezone information
  if (dateTime.endsWith("Z")) {
    dateTime = dateTime.slice(0, -1);
  }

  const date = dayjs.utc(dateTime).local(); // Convert UTC to local time
  const now = dayjs().startOf("day");
  const diffDays = now.diff(date.startOf("day"), "day");

  if (diffDays === 0) {
    // Today
    return includeTime ? `Today at ${date.format("h:mm A")}` : "Today";
  } else if (diffDays === 1) {
    // Yesterday
    return includeTime ? `Yesterday at ${date.format("h:mm A")}` : "Yesterday";
  } else if (diffDays < 7) {
    // Within the last week
    return includeTime
      ? `${date.format("dddd")} at ${date.format("h:mm A")}`
      : date.format("dddd");
  } else {
    // Different year
    return includeTime
      ? `${date.format("D MMM YYYY")} at ${date.format("h:mm A")}`
      : date.format("D MMM YYYY");
  }
}

// 4 hours ago
export function formatTimeAgo(dateTime: string): string {
  if (!dateTime) return "";

  // remove any trailing 'Z' or timezone information
  if (dateTime.endsWith("Z")) {
    dateTime = dateTime.slice(0, -1);
  }

  const date = dayjs.utc(dateTime).local(); // Convert UTC to local time
  const now = dayjs();
  const diffSeconds = now.diff(date, "second");
  const diffMinutes = now.diff(date, "minute");
  const diffHours = now.diff(date, "hour");
  const diffDays = now.diff(date, "day");
  const diffWeeks = now.diff(date, "week");
  const diffMonths = now.diff(date, "month");
  const diffYears = now.diff(date, "year");

  if (diffSeconds < 60) {
    return `${diffSeconds} ${diffSeconds === 1 ? 'second' : 'seconds'} ago`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
  } else {
    return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
  }
}

// today, yesterday, 3 Mar, 15 Jan 2023
export const formatShortDate = (dateString: string) => {
  // remove any trailing 'Z' or timezone information
  if (dateString.endsWith("Z")) {
    dateString = dateString.slice(0, -1);
  }

  const date = dayjs.utc(dateString).local();
  const now = dayjs().startOf("day");
  const diffDays = now.diff(date.startOf("day"), "day");

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (date.year() === now.year()) {
    return date.format("D MMM");
  } else {
    return date.format("D MMM YYYY");
  }
};

// Format duration to minutes and seconds
export const formatDuration = (seconds: number, unitText?: string) => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}${unitText ? ` ${unitText}` : " min"}`;
};

export const dateExpiredDate = (dateString: string, expiredDays: number): string => {
  // remove any trailing 'Z' or timezone information
  if (dateString.endsWith("Z")) {
    dateString = dateString.slice(0, -1);
  }

  const date = dayjs.utc(dateString).local();
  const expiredDate = date.add(expiredDays, "day");

  return expiredDate.format("D/M/YYYY");
}

export const dateExpiredDateInDay = (dateString: string, expiredDays: number) => {
  // remove any trailing 'Z' or timezone information
  if (dateString.endsWith("Z")) {
    dateString = dateString.slice(0, -1);
  }

  const date = dayjs.utc(dateString).local();
  const expiredDate = date.add(expiredDays, "day");
  const now = dayjs();

  const diffDays = expiredDate.diff(now, "day");

  return diffDays >= 0 ? diffDays.toString() : "0";
}

export const countDayNowBetweenDate = (dateString: string) => {
  const date = dayjs.utc(dateString).local().startOf("day");
  const now = dayjs().startOf("day");
  const diffDays = Math.abs(now.diff(date, "day"));
  return diffDays.toString();
}

// ─── MYT Timezone Utilities ─────────────────────────────────────

/**
 * Format a date in Malaysia Time (MYT / Asia/Kuala_Lumpur).
 * Accepts Date objects, ISO strings, or timestamps.
 */
export function formatMYT(date: Date | string | number, fmt: string): string {
  return dayjs(date).tz(MYT_TIMEZONE).format(fmt);
}

/**
 * Format a date as relative time ("2 hours ago") if within 24h,
 * otherwise as absolute date in MYT.
 */
export function formatMYTRelative(date: Date | string | number): string {
  const d = dayjs(date).tz(MYT_TIMEZONE);
  const now = dayjs().tz(MYT_TIMEZONE);
  const diffHours = now.diff(d, "hour");

  if (diffHours < 1) {
    const diffMinutes = now.diff(d, "minute");
    if (diffMinutes < 1) return "just now";
    return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  }
  return d.format("D MMM, h:mm A") + " MYT";
}
// JS Date is millisecond-precision, but BE timestamps carry microseconds —
// burst-written events (e.g. hiring-flow steps logged in one transaction) can
// share the same millisecond and tie under a getTime() sort, leaving them in
// arrival order. Tie-break on the raw ISO strings (uniform format + offset
// from the BE) so the microsecond ordering survives.
export const compareIsoDatesDesc = (a: string, b: string): number => {
  const diff = new Date(b).getTime() - new Date(a).getTime();
  if (diff !== 0) return diff;
  return b.localeCompare(a);
};
